import {
  getLanguageLabel,
  type Locale,
  locales,
  parseLocale,
} from "@repo/fika-shared/src/locale"
import { useNavigate } from "@solidjs/router"
import {
  createMemo,
  createSignal,
  For,
  Match,
  onMount,
  type ParentProps,
  Switch,
} from "solid-js"
import { useApplication } from "@/context/session-provider"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Checkbox } from "@/designSystem/components/checkbox"
import { FikaLogo } from "@/designSystem/components/fika-logo"
import { Select } from "@/designSystem/components/select"
import { Text } from "@/designSystem/components/text"
import { NameInputField } from "@/designSystem/patterns/name-input-field"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { createName } from "@/lib/create-name"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { captureEvent } from "@/lib/observability"
import { Blobs } from "./blobs"
import { ResponsiveImage } from "./responsive-image"
import {
  buttonWrapperClass,
  descriptionClass,
  formClass,
  imgWrapperClass,
  layoutClass,
  logoLinkClass,
  smallGapClass,
  titleClass,
} from "./writer-onboarding.css"

export function WriterOnboarding(props: ParentProps) {
  const { user } = useApplication()
  const navigate = useNavigate()
  const [showLanguageStep, setShowLanguageStep] = createSignal(false)

  onMount(() => {
    if (user.onboardingStage === "completed") {
      navigate("/")
    }
  })

  return (
    <Switch fallback={props.children}>
      <Match when={user.onboardingStage !== "completed"}>
        <SetUsername onComplete={() => setShowLanguageStep(true)} />
      </Match>
      <Match when={showLanguageStep()}>
        <SetLanguage onComplete={() => setShowLanguageStep(false)} />
      </Match>
    </Switch>
  )
}

function SetUsername(props: { onComplete: () => void }) {
  const { user } = useApplication()
  const { LL } = useI18nContext()
  const {
    userName,
    setUserName,
    nameStatus,
    validName,
    isFormValid,
    isButtonDisabled,
    nameDirty,
  } = createName()

  const UpsertPublicationMutation = graphql(
    `mutation UpsertPublication($input: UpsertPublicationInput!) {
      upsertPublication(input: $input) {
        ... on MutationUpsertPublicationSuccess {
          data {
            id
            onboardingStage
            publication {
              id
              name
              title
              locale
              additionalLanguages
            }
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )

  const [upsertPublicationState, upsertPublication] =
    createMutationWithErrorHandling(
      UpsertPublicationMutation,
      "upsertPublication",
    )

  async function onSubmit(e: SubmitEvent) {
    e.preventDefault()
    if (!isFormValid()) return

    const result = await upsertPublication({
      input: { name: userName() },
    })

    if (result.success) {
      props.onComplete()
    }
  }

  onMount(() => {
    captureEvent("onboarding_started", {})
  })

  return (
    <div class={layoutClass({ layout: "left" })}>
      <div class={logoLinkClass}>
        <FikaLogo withText />
      </div>
      <div class={imgWrapperClass}>
        <ResponsiveImage src="onboarding-umbrella" alt="Set username" />
        <Blobs />
      </div>
      <form onSubmit={onSubmit} class={formClass}>
        <h2 class={titleClass}>{LL().onboarding.buildAudience()}</h2>
        <div class={smallGapClass}>
          <p class={descriptionClass}>{LL().onboarding.ownYourContent()}</p>
          <p class={descriptionClass}>{LL().onboarding.nameYourBlog()}</p>
        </div>
        <div class={smallGapClass}>
          <NameInputField
            userName={userName}
            setUserName={setUserName}
            nameStatus={nameStatus}
            nameDirty={nameDirty}
            validName={validName}
            isFormValid={isFormValid}
          />
        </div>
        <div class={buttonWrapperClass}>
          <Button
            hue="emerald"
            iconName="book-open-text"
            type="submit"
            disabled={isButtonDisabled()}
            active={upsertPublicationState.fetching}
          >
            {user.publication?.name
              ? nameDirty()
                ? LL().onboarding.updateBlog()
                : LL().onboarding.continue()
              : LL().onboarding.createBlog()}
          </Button>
        </div>
      </form>
    </div>
  )
}

function SetLanguage(props: { onComplete: () => void }) {
  const { user } = useApplication()
  const { LL } = useI18nContext()
  const navigate = useNavigate()

  const [locale, setLocale] = createSignal<Locale>(
    parseLocale(user.publication?.locale ?? "en"),
  )
  const [enabledLocales, setEnabledLocales] = createSignal<Set<Locale>>(
    new Set(),
  )

  const alternativeLocales = createMemo(() =>
    locales
      .filter((l: Locale) => l !== locale())
      .sort((a, b) => getLanguageLabel(a).localeCompare(getLanguageLabel(b))),
  )

  function handleLocaleChange(newLocale: string | null) {
    if (!newLocale) return
    const l = parseLocale(newLocale)
    const prev = locale()
    setLocale(l)
    setEnabledLocales(current => {
      const next = new Set(current)
      next.add(prev)
      next.delete(l)
      return next
    })
  }

  function toggleLocale(l: Locale) {
    setEnabledLocales(prev => {
      const next = new Set(prev)
      if (next.has(l)) {
        next.delete(l)
      } else {
        next.add(l)
      }
      return next
    })
  }

  const allAlternativesEnabled = createMemo(() =>
    alternativeLocales().every(l => enabledLocales().has(l)),
  )

  const UpdateLanguageMutation = graphql(
    `mutation UpdateOnboardingLanguage($input: UpdatePublicationInput!) {
      updateCurrentPublication(input: $input) {
        ... on MutationUpdateCurrentPublicationSuccess {
          data {
            id
            locale
            additionalLanguages
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )

  const [updateState, updatePublication] = createMutationWithErrorHandling(
    UpdateLanguageMutation,
    "updateCurrentPublication",
  )

  async function onSubmit(e: SubmitEvent) {
    e.preventDefault()

    const additionalLanguages = allAlternativesEnabled()
      ? null
      : Array.from(enabledLocales())

    const result = await updatePublication({
      input: { locale: locale(), additionalLanguages },
    })

    if (result.success) {
      captureEvent("onboarding_completed", {})
      props.onComplete()
      navigate("/")
    }
  }

  return (
    <div class={layoutClass({ layout: "right" })}>
      <div class={logoLinkClass}>
        <FikaLogo withText />
      </div>
      <div class={imgWrapperClass}>
        <ResponsiveImage src="onboarding-sky" alt="Choose language" />
        <Blobs />
      </div>
      <form onSubmit={onSubmit} class={formClass}>
        <div class={smallGapClass}>
          <h2 class={titleClass}>{LL().onboarding.chooseLanguage.title()}</h2>
          <p class={descriptionClass}>
            {LL().onboarding.chooseLanguage.description()}
          </p>
        </div>
        <div class={smallGapClass}>
          <Select
            hue="amber"
            label={LL().blog.language()}
            options={locales
              .map(l => ({
                value: l,
                label: getLanguageLabel(l),
                disabled: false,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))}
            value={locale}
            onChange={handleLocaleChange}
          />
          <Box gap="12">
            <Text fontSize="s" fontWeight="500">
              {LL().blog.additionalLanguages()}
            </Text>
            <For each={alternativeLocales()}>
              {l => (
                <Checkbox
                  label={getLanguageLabel(l)}
                  hue="amber"
                  checked={enabledLocales().has(l)}
                  onChange={() => toggleLocale(l)}
                  disabled={updateState.fetching}
                />
              )}
            </For>
          </Box>
        </div>
        <div class={buttonWrapperClass}>
          <Button hue="emerald" type="submit" active={updateState.fetching}>
            {LL().onboarding.continue()}
          </Button>
        </div>
      </form>
    </div>
  )
}
